import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import {
  Container,
  Eyebrow,
  RevealHeading,
  Reveal,
  PillButton,
  TextLink,
  Section,
} from "@/components/primitives";
import { FlowDiagram } from "@/components/FlowDiagram";
import { NigeriaMap } from "@/components/NigeriaMap";
import { DeliveryRadar } from "@/components/DeliveryRadar";
import { Marquee } from "@/components/Marquee";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import {
  IMAGES,
  HERO_FLOW,
  CAPABILITIES,
  MODEL,
  PUBLIC_IMPACT,
  FRAMEWORK_DIMENSIONS,
  BETTER_GOV,
  WHO_WE_WORK_WITH,
} from "@/data/site";

/* ------------------------------- HERO ------------------------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-govx-black text-govx-paper grain pt-[74px]">
      <Container className="relative z-10 grid min-h-[92vh] items-center gap-12 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">
        {/* Left */}
        <div>
          <Eyebrow dark>Public Sector Strategy &amp; Implementation</Eyebrow>
          <RevealHeading
            lines={["Turning Public", "Ambition Into", "Measurable Impact."]}
            className="mt-8 font-display text-[2.6rem] font-medium leading-[0.98] tracking-tight text-balance sm:text-6xl lg:text-7xl"
            delay={0.1}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-govx-grey sm:text-lg"
          >
            GovX Nigeria works with governments, political organisations and public leaders to
            turn ideas, policies and priorities into coordinated action — and measurable outcomes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <PillButton to="/contact" data-testid="hero-primary-cta">Start a Conversation</PillButton>
            <PillButton to="/what-we-do" variant="ghost" dark data-testid="hero-secondary-cta">
              Explore Our Capabilities
            </PillButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-14 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-6"
          >
            {[["Government", "Delivery"], ["Political", "Operations"], ["Impact", "Measurement"]].map(([a, b]) => (
              <div key={a}>
                <div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-govx-lime">{a}</div>
                <div className="mt-1 text-sm font-medium text-govx-paper/80">{b}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — image + system diagram */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden border border-white/10 sm:aspect-[5/6]">
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.lagosVI}
              alt="Lagos, Nigeria waterfront skyline"
              className="h-[118%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-govx-black via-govx-black/50 to-transparent" />
          </div>

          {/* glass system diagram — stacked below on mobile, overlaid on desktop */}
          <div className="mt-4 rounded-sm border border-white/12 bg-govx-charcoal/90 p-5 backdrop-blur-xl lg:absolute lg:inset-x-6 lg:bottom-6 lg:mt-0 lg:bg-govx-black/55">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-govx-grey">
                The Public Impact System
              </span>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-govx-lime" />
            </div>
            <FlowDiagram steps={HERO_FLOW} dark compact />
          </div>
        </motion.div>
      </Container>

      <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] opacity-[0.5]">
        <NigeriaMap stroke="rgba(255,255,255,0.06)" animate={false} />
      </div>
    </section>
  );
};

/* ----------------------------- THE PROBLEM ----------------------------- */
const Problem = () => (
  <Section className="py-24 lg:py-36">
    <Container className="grid gap-14 lg:grid-cols-[1fr_0.9fr] lg:items-center">
      <div>
        <Eyebrow>The Problem</Eyebrow>
        <RevealHeading
          lines={["Good Ideas Don't", "Deliver Themselves."]}
          as="h2"
          className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
        />
        <Reveal delay={0.1}>
          <p className="mt-7 max-w-lg text-base leading-relaxed text-govx-charcoal/70 sm:text-lg">
            Governments and political organisations rarely struggle because they lack ideas. The
            harder challenge is turning priorities into coordinated execution.
          </p>
        </Reveal>
        <div className="mt-9 space-y-0 border-t border-govx-charcoal/10">
          {[
            "Plans need owners.",
            "Programmes need systems.",
            "Teams need visibility.",
            "Projects need monitoring.",
            "Citizens need to be heard.",
          ].map((line, i) => (
            <Reveal key={line} delay={i * 0.06}>
              <div className="flex items-center gap-4 border-b border-govx-charcoal/10 py-4">
                <span className="font-mono text-xs text-govx-green">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-lg font-medium tracking-tight sm:text-xl">{line}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 font-display text-xl font-medium tracking-tight text-govx-green">
            GovX exists to close that gap.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="relative">
          <div className="relative aspect-[3/4] overflow-hidden border border-govx-charcoal/10">
            <img src={IMAGES.construction} alt="Nigerian infrastructure under construction" className="h-full w-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden w-56 border border-govx-charcoal/10 bg-govx-paper p-5 shadow-sm sm:block">
            <div className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-govx-grey">Idea → Outcome</div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs font-medium text-govx-charcoal">
              {["Idea", "Plan", "People", "Execution", "Data", "Outcome"].map((s, i, a) => (
                <span key={s} className="flex items-center gap-1.5">
                  {s}
                  {i < a.length - 1 && <ArrowRight className="h-3 w-3 text-govx-green" />}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </Container>
  </Section>
);

/* ----------------------------- WHAT WE DO ----------------------------- */
const CapabilityCard = ({ item }) => (
  <Reveal>
    <Link
      to={item.to}
      data-testid={`capability-${item.id}`}
      className="group relative flex h-full flex-col overflow-hidden border border-white/10 bg-govx-charcoal/40 transition-colors duration-500 hover:border-govx-lime/50"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-govx-charcoal to-transparent" />
        <span className="absolute left-5 top-5 font-mono text-xs text-govx-lime">{item.index}</span>
      </div>
      <div className="flex flex-1 flex-col p-6 lg:p-7">
        <h3 className="font-display text-2xl font-medium tracking-tight text-govx-paper">{item.title}</h3>
        <p className="mt-2 text-sm font-medium text-govx-lime">{item.tagline}</p>
        <p className="mt-4 flex-1 text-sm leading-relaxed text-govx-grey">{item.body}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-govx-paper transition-colors group-hover:text-govx-lime">
          {item.cta}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

const WhatWeDo = () => (
  <Section dark className="py-24 lg:py-36">
    <Container className="relative z-10">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow dark>What We Do</Eyebrow>
          <RevealHeading
            lines={["From Strategy", "To Execution."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-govx-grey">
            Four connected practices — one operating methodology. We work across the entire
            journey from idea to impact.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {CAPABILITIES.map((item) => (
          <CapabilityCard key={item.id} item={item} />
        ))}
      </div>
    </Container>
  </Section>
);

/* ----------------------------- GOVX MODEL ----------------------------- */
const GovxModel = () => (
  <Section className="py-24 lg:py-36">
    <Container>
      <Eyebrow>The GovX Model</Eyebrow>
      <RevealHeading
        lines={["Diagnose. Design.", "Deliver. Measure."]}
        as="h2"
        className="mt-7 max-w-3xl font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
      />
      <div className="mt-16 grid gap-px overflow-hidden border border-govx-charcoal/10 bg-govx-charcoal/10 md:grid-cols-5">
        {MODEL.map((step, i) => (
          <motion.div
            key={step.n}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="group relative bg-govx-paper p-7 transition-colors duration-500 hover:bg-govx-charcoal hover:text-govx-paper"
          >
            <div className="font-mono text-4xl font-light text-govx-grey transition-colors group-hover:text-govx-lime">
              {step.n}
            </div>
            <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{step.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-govx-charcoal/60 transition-colors group-hover:text-govx-paper/70">
              {step.body}
            </p>
          </motion.div>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10">
          <TextLink to="/what-we-do">How We Work</TextLink>
        </div>
      </Reveal>
    </Container>
  </Section>
);

/* --------------------------- NIGERIA AT SCALE --------------------------- */
const NigeriaScale = () => (
  <Section dark className="overflow-hidden py-24 lg:py-36">
    <Container className="relative z-10">
      <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <Eyebrow dark>Nigeria</Eyebrow>
          <RevealHeading
            lines={["Nigeria Is", "Building At Scale."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-govx-grey sm:text-lg">
              From infrastructure and digital transformation to healthcare, education, industry and
              public services, Nigeria is navigating a period of rapid institutional and economic
              change.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-lg font-display text-xl font-medium tracking-tight text-govx-paper">
              The challenge is no longer only ambition. It is execution.
            </p>
            <p className="mt-4 text-govx-lime">GovX works where policy meets implementation.</p>
          </Reveal>
        </div>

        {/* Feature map */}
        <Reveal delay={0.15}>
          <div className="relative overflow-hidden rounded-sm border border-white/10 bg-gradient-to-b from-govx-charcoal/50 to-govx-black p-5 sm:p-7">
            <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-govx-grey">
                Federal Republic of Nigeria
              </span>
              <span className="flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.16em] text-govx-lime">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-govx-lime" />
                Nationwide
              </span>
            </div>
            <NigeriaMap variant="feature" className="h-[320px] w-full sm:h-[440px]" />
            <p className="mt-3 border-t border-white/10 pt-3 font-mono text-[0.62rem] leading-relaxed text-govx-grey">
              GovX works with institutions across all 36 states and the FCT. Tap any state to see its name.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Editorial imagery */}
      <div className="mt-16 grid gap-4 md:grid-cols-3">
        {[IMAGES.lagosVIblue, IMAGES.nassComplex, IMAGES.kadunaAerial].map((src, i) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative overflow-hidden border border-white/10 ${
              i === 0 ? "aspect-[16/11] md:col-span-2" : "aspect-[16/11]"
            }`}
          >
            <img
              src={src}
              alt="Nigeria building at scale"
              className="h-full w-full object-cover grayscale transition-all duration-[900ms] group-hover:scale-[1.04] group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-govx-black/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </motion.div>
        ))}
      </div>
    </Container>
  </Section>
);

/* --------------------------- PUBLIC IMPACT --------------------------- */
const PublicImpact = () => (
  <Section className="py-24 lg:py-36">
    <Container>
      <Eyebrow>Public Impact</Eyebrow>
      <RevealHeading
        lines={["Policy → Delivery → Impact"]}
        as="h2"
        className="mt-7 font-display text-3xl font-medium leading-tight tracking-tight sm:text-5xl"
      />
      <div className="mt-16 grid gap-px overflow-hidden border border-govx-charcoal/10 bg-govx-charcoal/10 sm:grid-cols-5">
        {PUBLIC_IMPACT.map((item, i) => (
          <motion.div
            key={item.k}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="group relative bg-govx-paper p-7"
          >
            <div className="mb-8 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-govx-green transition-transform duration-500 group-hover:scale-150" />
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.16em] text-govx-grey">
                Stage {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <h3 className="font-display text-2xl font-medium tracking-tight">{item.k}</h3>
            <p className="mt-3 text-sm text-govx-charcoal/60">{item.q}</p>
            {i < PUBLIC_IMPACT.length - 1 && (
              <ArrowDown className="mt-6 h-4 w-4 text-govx-green sm:hidden" />
            )}
          </motion.div>
        ))}
      </div>
    </Container>
  </Section>
);

/* ------------------------ SIGNATURE FRAMEWORK ------------------------ */
const Framework = () => (
  <Section dark className="py-24 lg:py-36">
    <Container className="relative z-10">
      <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
        <div>
          <Eyebrow dark>Signature Framework</Eyebrow>
          <RevealHeading
            lines={["The GovX", "Delivery Framework"]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-govx-grey sm:text-lg">
              A practical framework for understanding whether a public programme is positioned to
              deliver — and where implementation is breaking down.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/5 sm:grid-cols-2">
            {FRAMEWORK_DIMENSIONS.map((dim, i) => (
              <motion.div
                key={dim}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex items-center justify-between bg-govx-black px-5 py-4"
              >
                <span className="font-display text-sm font-medium text-govx-paper">{dim}</span>
                <span className="font-mono text-[0.62rem] text-govx-grey">D{i + 1}</span>
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10">
              <TextLink to="/impact" dark>Explore the Framework</TextLink>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="border border-white/10 bg-govx-charcoal/40 p-6 lg:p-8">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-govx-grey">
                GovX Delivery Index
              </span>
              <span className="font-mono text-[0.62rem] text-govx-lime">Illustrative</span>
            </div>
            <DeliveryRadar />
            <p className="mt-4 border-t border-white/10 pt-4 text-xs text-govx-grey">
              How well is a programme actually being delivered? Seven dimensions, one view. Sample
              geometry shown — not real scores.
            </p>
          </div>
        </Reveal>
      </div>
    </Container>
  </Section>
);

/* --------------------- BETTER GOVERNMENT --------------------- */
const BetterGov = () => (
  <Section className="py-24 lg:py-36">
    <Container>
      <Eyebrow>The Work We Do</Eyebrow>
      <RevealHeading
        lines={["What Does Better", "Government Look Like?"]}
        as="h2"
        className="mt-7 max-w-3xl font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
      />
      <div className="mt-16 grid gap-12 md:grid-cols-3">
        {BETTER_GOV.map((item, i) => (
          <Reveal key={item.n} delay={i * 0.1}>
            <div className="border-t border-govx-charcoal/15 pt-6">
              <div className="font-mono text-6xl font-light text-govx-green">{item.n}</div>
              <h3 className="mt-6 font-display text-2xl font-medium tracking-tight">{item.title}</h3>
              <p className="mt-3 text-base leading-relaxed text-govx-charcoal/65">{item.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-14 font-display text-2xl font-medium tracking-tight text-govx-green sm:text-3xl">
          That's the work we do.
        </p>
      </Reveal>
    </Container>
  </Section>
);

/* --------------------- WHO WE WORK WITH --------------------- */
const WhoWeWorkWith = () => (
  <Section dark className="py-24 lg:py-36">
    <Container>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow dark>Who We Work With</Eyebrow>
          <RevealHeading
            lines={["The Institutions", "Shaping Nigeria."]}
            as="h2"
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl"
          />
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-govx-grey">
            We work across government, politics and public impact — with the people responsible
            for decisions and the teams responsible for delivery.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-px overflow-hidden border border-white/10 bg-white/5 md:grid-cols-3">
        {WHO_WE_WORK_WITH.map((col, i) => (
          <motion.div
            key={col.group}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="bg-govx-black p-8 lg:p-10"
          >
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-govx-lime">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-display text-xl font-medium tracking-tight text-govx-paper">{col.group}</h3>
            </div>
            <ul className="mt-7 space-y-0">
              {col.items.map((it) => (
                <li key={it} className="flex items-center gap-3 border-t border-white/8 py-3.5 text-sm text-govx-paper/80">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-govx-green" />
                  {it}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10">
          <TextLink to="/partner" dark>Explore partnership options</TextLink>
        </div>
      </Reveal>
    </Container>
  </Section>
);

/* ------------------------------- PAGE ------------------------------- */
export default function Home() {
  return (
    <>
      <Seo
        title="GovX Nigeria | Governance. Strategy. Delivery."
        description="GovX Nigeria helps governments, political organisations and public leaders turn strategy and policy into coordinated execution and measurable impact."
      />
      <Hero />
      <Marquee />
      <Problem />
      <WhatWeDo />
      <GovxModel />
      <NigeriaScale />
      <WhoWeWorkWith />
      <PublicImpact />
      <Framework />
      <BetterGov />
      <CTASection
        title={["Turning public ambition", "into measurable impact."]}
        copy="Tell us what you're trying to build, fix or deliver. Let's map the problem and build a delivery system around it."
      />
    </>
  );
}
